import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useDrawerOpenState } from '../../hooks/useEmbeddedHost';
import { useLayoutData } from '../../hooks/useLayoutContext';
import { useEmbeddedAppearance } from '../../hooks/useEmbeddedAppearance';
import { useTranslation } from '../../i18n/i18n';
import { shellSizeStyle } from './shellSizes';
import { QrOnScreenContext } from '../../hooks/useQrOnScreen';
import EmbeddedCrashBoundary from './CrashBoundary';

/** Dims the website behind the sheet. */
const BACKDROP = 'absolute inset-0 bg-[rgba(15,20,30,0.45)]';

/** 12px taller than the card, for the touch-height help row. */
const DRAWER = shellSizeStyle({
  minHeight: 282,
  maxHeight: 452,
  borderWidth: 0,
  radius: 0,
  helpRowHeight: 44,
  linkHeight: 44,
  linkPaddingX: 8,
});

/**
 * EmbeddedDrawerShell — Phone mode: the iframe covers the screen and this draws
 * the dimmed backdrop and the bottom sheet. Closing hides the iframe through
 * the loader; the flow stays mounted so reopening continues where it was.
 *
 * In the Studio's preview it is the same backdrop and sheet, open from the
 * start, without closing on a tap or Escape, and never taking focus from the
 * Studio's form.
 */
export default function EmbeddedDrawerShell({
  children,
  preview,
}: {
  children: ReactNode;
  /** The Studio's preview: open, and it stays open. */
  preview?: boolean;
}) {
  const { t } = useTranslation();
  const { requestType } = useLayoutData();
  const { open, close } = useDrawerOpenState(preview);
  const appearance = useEmbeddedAppearance('drawer');
  const sheetRef = useRef<HTMLDivElement>(null);
  const [qrShown, setQrShown] = useState(false);

  useEffect(() => {
    if (!open || preview) return;
    sheetRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close, preview]);

  return (
    <QrOnScreenContext.Provider
      value={{ shown: qrShown, setShown: setQrShown }}
    >
      <div hidden={!open} className="fixed inset-0 font-inter">
        {preview ? (
          <div aria-hidden="true" className={BACKDROP} />
        ) : (
          <button
            type="button"
            tabIndex={-1}
            aria-label={t('embedded.drawer.close')}
            onClick={close}
            className={BACKDROP}
          />
        )}
        <div
          ref={sheetRef}
          role="dialog"
          aria-modal="true"
          aria-label={t(
            requestType === 'issuance'
              ? 'embedded.drawer.labelIssuance'
              : 'embedded.drawer.label',
          )}
          tabIndex={-1}
          style={{ ...DRAWER, ...appearance }}
          className="animate-embedded-up absolute inset-x-0 bottom-0 rounded-t-(--embedded-sheet-radius,20px) bg-card pt-2 pb-[calc(26px+env(safe-area-inset-bottom))] shadow-[0_-8px_32px_rgba(0,0,0,0.18)] outline-none"
        >
          <div
            aria-hidden="true"
            className="mx-auto mb-1 h-[5px] w-9 rounded-full bg-border"
          />
          <EmbeddedCrashBoundary>{children}</EmbeddedCrashBoundary>
        </div>
      </div>
    </QrOnScreenContext.Provider>
  );
}
